import {
  Build as BuildIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchAdminVoices,
  fetchVoiceLanguages,
  initializeVoices,
  reinitializeVoices,
  setDefaultVoiceTiers,
  updateVoiceStatus,
} from "../store/slices/adminVoicesSlice";

import type { ReactElement, ChangeEvent } from "react";

// Voices management admin page
// NOTE: Restored component wrapper after previous corruption removed function definition
const VoicesPage = (): ReactElement => {
  const dispatch = useAppDispatch();
  const {
    voices = [],
    loading = false,
    error = null,
    languages = [],
  } = useAppSelector((state) => state.adminVoices || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [operationError, setOperationError] = useState<string | null>(null);

  type ChipColor =
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "info";

  useEffect(() => {
    // Load available languages on component mount
    dispatch(fetchVoiceLanguages());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchAdminVoices({
        page: 1,
        limit: 100,
        language: languageFilter || undefined,
        tier: tierFilter || undefined,
      }),
    );
  }, [dispatch, searchTerm, languageFilter, tierFilter]);

  const handleStatusChange = async (
    voiceId: string,
    newStatus: boolean,
  ): Promise<void> => {
    setOperationError(null);
    const result = await dispatch(
      updateVoiceStatus({ id: voiceId, enabled: newStatus }),
    );
    if (updateVoiceStatus.rejected.match(result)) {
      setOperationError(result.payload || "Failed to update voice status");
    } else {
      dispatch(
        fetchAdminVoices({
          language: languageFilter || undefined,
          tier: tierFilter || undefined,
        }),
      );
    }
  };

  const handleStatusToggle = (voiceId: string, currentStatus: string): void => {
    const newEnabled = currentStatus !== "ACTIVE";
    void handleStatusChange(voiceId, newEnabled);
  };

  const handleInitializeVoices = async (): Promise<void> => {
    setOperationError(null);
    const result = await dispatch(initializeVoices());
    if (initializeVoices.rejected.match(result)) {
      setOperationError(result.payload || "Failed to initialize voices");
    } else {
      dispatch(
        fetchAdminVoices({
          language: languageFilter || undefined,
          tier: tierFilter || undefined,
        }),
      );
    }
  };

  const handleReinitializeVoices = async (): Promise<void> => {
    setOperationError(null);
    const result = await dispatch(reinitializeVoices());
    if (reinitializeVoices.rejected.match(result)) {
      setOperationError(result.payload || "Failed to reinitialize voices");
    } else {
      dispatch(
        fetchAdminVoices({
          language: languageFilter || undefined,
          tier: tierFilter || undefined,
        }),
      );
    }
  };

  const handleSetDefaultTiers = async (): Promise<void> => {
    setOperationError(null);
    const result = await dispatch(setDefaultVoiceTiers());
    if (setDefaultVoiceTiers.rejected.match(result)) {
      setOperationError(result.payload || "Failed to set default tiers");
    } else {
      dispatch(
        fetchAdminVoices({
          language: languageFilter || undefined,
          tier: tierFilter || undefined,
        }),
      );
    }
  };

  const getTierColor = (tier: string): ChipColor => {
    switch (tier) {
      case "BASIC":
        return "success";
      case "PREMIUM":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: string): ChipColor => {
    return status === "ACTIVE" ? "success" : "default";
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Voice management
      </Typography>

      {/* Voice Management Actions */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Voice configuration management
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<BuildIcon />}
            onClick={handleInitializeVoices}
            disabled={loading}
            color="primary"
          >
            Initialize voices
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReinitializeVoices}
            disabled={loading}
            color="info"
          >
            Reinitialize
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={handleSetDefaultTiers}
            disabled={loading}
            color="secondary"
          >
            Configure tiers
          </Button>
        </Stack>
      </Box>

      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search voices..."
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Language</InputLabel>
          <Select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            label="Language"
          >
            <MenuItem value="">All languages</MenuItem>
            {languages.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.name} ({lang.count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            label="Type"
          >
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="BASIC">Basic</MenuItem>
            <MenuItem value="PREMIUM">Premium</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {(error || operationError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {operationError || error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Voice ID</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : voices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No voices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                voices.map((voice) => (
                  <TableRow key={voice.id}>
                    <TableCell>{voice.name}</TableCell>
                    <TableCell>{voice.language}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          voice.gender === "FEMALE"
                            ? "Female"
                            : voice.gender === "MALE"
                              ? "Male"
                              : voice.gender
                        }
                        size="small"
                        color={
                          voice.gender === "FEMALE" ? "secondary" : "primary"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          voice.tier === "BASIC"
                            ? "Basic"
                            : voice.tier === "PREMIUM"
                              ? "Premium"
                              : voice.tier
                        }
                        size="small"
                        color={getTierColor(voice.tier)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          voice.status === "ACTIVE" ? "Active" : "Inactive"
                        }
                        size="small"
                        color={getStatusColor(voice.status)}
                        onClick={() =>
                          handleStatusToggle(voice.id, voice.status)
                        }
                        clickable
                      />
                    </TableCell>
                    <TableCell>{voice.provider}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {voice.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <PlayIcon />
                      </IconButton>
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default VoicesPage;
